import { ReactElement } from 'react';
import { 
  Leader, ThunkDispatchType, actions, OrgUserAutocompleteOption, OrganizationLeader,
  SharedLeaderGrant, StandardLeaderPermissions, CategoryLeaderPermissions, SchedulingPermission
} from '../../store';
import { PermissionsFormData } from './PermissionsForm';
import { useDispatch } from 'react-redux';
import ShareLeader from './Sharing';
import { core } from '../../store/cabinetApi/core';
import { appCore } from '../../store/cabinetApi/appCore';
import { profiles } from '../../store/cabinetApi/profiles';


interface Props {
  leader: Leader | OrganizationLeader;
  isOpen: boolean;
  onDidDismiss: () => void;
  isAdminUser?: boolean;
}

export const ShareLeaderContainer = ({ leader, isOpen, onDidDismiss, isAdminUser }: Props): ReactElement => {
  const dispatch = useDispatch<ThunkDispatchType>();

  const { data: userAutocompleteData } = core.useCoreOrgUserAutocompleteListQuery();
  const { sharedLeaderGrants: userSharedLeaderGrants } = appCore.useAppCoreSharedLeadersListQuery(
    undefined,
    {
      skip: isAdminUser,
      selectFromResult: ({ data }) => ({
        sharedLeaderGrants: (data || []).filter((grant) => grant.leader === leader.id),
      }),
    }
  );
  const { data: orgSharedLeaderGrantsList = [] } = appCore.useAppCoreOrgSharedLeadersListQuery(
    undefined,
    { skip: !isAdminUser }
  );
  const sharedLeaderGrants = isAdminUser
    ? orgSharedLeaderGrantsList.filter((grant) => grant.leader === leader.id)
    : userSharedLeaderGrants;
  const { data: leaderProfileCategories } = profiles.useAppProfilesCategoriesListQuery(leader.id);

  const [shareLeader] = appCore.useAppCoreLeadersShareCreateMutation();
  const [adminShareLeader] = appCore.useAppCoreLeadersAdminShareLeaderCreateMutation();
  const [updateSharedLeader] = appCore.useAppCoreSharedLeadersPartialUpdateMutation();
  const [revokeSharedLeader] = appCore.useAppCoreSharedLeadersRevokePartialUpdateMutation();
  const [updateOrgSharedLeader] = appCore.useAppCoreOrgSharedLeadersPartialUpdateMutation();
  const [revokeOrgSharedLeader] = appCore.useAppCoreOrgSharedLeadersRevokePartialUpdateMutation();

  const handleShare = async (userIds: OrgUserAutocompleteOption['value'][],
    standardPermissions: StandardLeaderPermissions,
    categoryPermissions: CategoryLeaderPermissions,
    schedulingPermissions: SchedulingPermission,
    crmAllowAllContacts: boolean,
    startDate: string, endDate: string | null, isAdmin: boolean,
  ) => {
    const mutateSharedLeaderGrantRequest = {
      collaboratorIds: userIds,
      permissions: standardPermissions as unknown as { [key: string]: { [key: string]: boolean } },
      category_permissions: categoryPermissions as unknown as { [key: string]: { [key: string]: boolean } },
      scheduling_permissions: schedulingPermissions as unknown as { [key: string]: boolean },
      crm_contact_permissions: crmAllowAllContacts
        ? { view: true, add: true, change: true, delete: true }
        : { view: false, add: false, change: false, delete: false },
      startDate,
      endDate,
      isAdmin,
    };

    if (isAdminUser) {
      await adminShareLeader({
        id: leader.id,
        mutateSharedLeaderGrantRequest,
      }).unwrap();
    } else {
      await shareLeader({
        id: leader.id,
        mutateSharedLeaderGrantRequest,
      }).unwrap();
    }
    
    if (isAdminUser) {
      dispatch(actions.leaders.fetchLeaders());
    }
  };

  const handleRevoke = async (grantId: SharedLeaderGrant['id'], leaderId: Leader['id']) => {
    if (isAdminUser) {
      await revokeOrgSharedLeader({
        id: Number(grantId),
        patchedSharedLeaderGrant: {},
      }).unwrap();
    } else {
      await revokeSharedLeader({
        id: grantId.toString(),
        patchedSharedLeaderGrant: {},
      }).unwrap();
    }
    if (isAdminUser) {
      dispatch(actions.leaders.fetchLeaders());
    }
  };

  const handleUpdateLeaderEmails = async (leaders: Leader[]) => {
    const leaderUpdatePromises: Promise<Leader | undefined>[] = [];
    leaders.forEach(l => {
      const leaderUpdate = {id: l.id, email: l.email};
      leaderUpdatePromises.push(dispatch(actions.leaders.updateLeader(leaderUpdate, null)));
    });
    await Promise.resolve(leaderUpdatePromises);
  };

  const handleEditPermissions = async (grantId: SharedLeaderGrant['id'], permissionsData: PermissionsFormData) => {
    const patch = {
      start_date: permissionsData.startDate,
      end_date: permissionsData.endDate,
      is_admin: permissionsData.permissionType === 'all',
      leader: leader.id,
      permissions_data: permissionsData.standardPermissions,
      category_permissions_data: permissionsData.customCategoryPermissions,
      scheduling_permissions_data: {
        view: permissionsData.schedulingPermissionEdit,
        change: permissionsData.schedulingPermissionEdit,
        add_to_meeting: permissionsData.schedulingPermissionAddToMeetings,
      },
      crm_contact_permissions_data: permissionsData.crmAllowAllContacts
        ? { view: true, add: true, change: true, delete: true }
        : { view: false, add: false, change: false, delete: false },
    };
    if (isAdminUser) {
      await updateOrgSharedLeader({
        id: Number(grantId),
        patchedSharedLeaderGrant: patch,
      }).unwrap();
    } else {
      await updateSharedLeader({
        id: grantId.toString(),
        patchedSharedLeaderGrant: patch,
      }).unwrap();
    }
    if (isAdminUser) {
      dispatch(actions.leaders.fetchLeaders());
    }
  };


  return (
    <ShareLeader
      leader={leader}
      isOpen={isOpen}
      onDidDismiss={onDidDismiss}
      sharedLeaderGrants={sharedLeaderGrants}
      shareWithUserOptions={userAutocompleteData || []}
      leaderProfileCategories={leaderProfileCategories || {}}
      onShare={handleShare}
      onRevoke={handleRevoke}
      onUpdateLeaderEmails={handleUpdateLeaderEmails}
      onEditPermissions={handleEditPermissions}
    />
  );
};

export default ShareLeaderContainer;
